import { formatDistanceToNow } from 'date-fns';
import { Avatar, Card, CardContent, CardHeader, CardTitle, initialsFromName } from '@mlm/ui';
import type { ActivityRow } from '../../lib/api';
import { TenantWideBadge } from './tenant-wide-badge';

function safeRelative(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return formatDistanceToNow(d, { addSuffix: true });
}

/**
 * The activity tape. Under a branch filter the order rows narrow but
 * enrolments and payouts can't (no branch on `slots` / `payout_requests`), so
 * they stay and carry a badge — a feed showing nothing but orders would read as
 * a broken filter rather than an honest one.
 */
export function ActivityFeed({
  rows,
  filtered = false,
}: {
  rows: ActivityRow[];
  filtered?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-h3">Activity</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No activity yet.</p>
        ) : (
          <ul className="divide-y">
            {rows.map((r, i) => (
              <li key={i} className="flex items-center gap-3 px-6 py-3">
                <Avatar size="sm" initials={initialsFromName(r.actorName)} />
                <p className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 text-sm">
                  <span>
                    <span className="font-medium">{r.actorName}</span>{' '}
                    <span className="text-muted-foreground">{r.summary}</span>
                  </span>
                  <TenantWideBadge show={filtered && r.scope === 'tenant'} />
                </p>
                <time
                  dateTime={r.ts}
                  className="text-xs text-muted-foreground tabular-nums shrink-0"
                  title={(() => {
                    const d = new Date(r.ts);
                    return Number.isNaN(d.getTime()) ? r.ts : d.toLocaleString();
                  })()}
                >
                  {safeRelative(r.ts)}
                </time>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
